import React, { useEffect, useState } from "react";
// Optional: reuse same styling file as Create modal
import "./style/CreateJobModal.css";

const initialRequirements = {
  education: { minimum: "", preferred: "" },
  experience: { minimumYears: 0, preferredYears: 0 },
  certifications: { mandatory: [], preferred: [] },
  skills: { mandatory: [], preferred: [] },
  other: "",
};

const EditJobModal = ({ job, open, onClose, onSave }) => {
  const [form, setForm] = useState({
    title: "",
    department: "",
    location: "",
    locationType: "ONSITE",
    type: "FULL_TIME",
    openings: 1,
    description: "",
    keyResponsibilities: "",
    validityDays: 30,
  });

  const [requirements, setRequirements] = useState(initialRequirements);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!job) return;

    setForm({
      title: job.title || "",
      department: job.department || "",
      location: job.location || "",
      locationType: job.locationType || "ONSITE",
      type: job.type || "FULL_TIME",
      openings: Number(job.openings ?? 1),
      description: job.description || "",
      keyResponsibilities: job.keyResponsibilities || "",
      validityDays: Number(job.validityDays ?? 30),
    });

    setRequirements({
      education: {
        minimum: job.requirements?.education?.minimum || "",
        preferred: job.requirements?.education?.preferred || "",
      },
      experience: {
        minimumYears: job.requirements?.experience?.minimumYears ?? 0,
        preferredYears: job.requirements?.experience?.preferredYears ?? 0,
      },
      certifications: {
        mandatory: job.requirements?.certifications?.mandatory || [],
        preferred: job.requirements?.certifications?.preferred || [],
      },
      skills: {
        mandatory: job.requirements?.skills?.mandatory || [],
        preferred: job.requirements?.skills?.preferred || [],
      },
      other: job.requirements?.other || "",
    });

    setError("");
    setSuccess("");
  }, [job]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(""), 4500);
    return () => clearTimeout(t);
  }, [success]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    // keep numbers as numbers
    if (type === "number") {
      setForm((prev) => ({ ...prev, [name]: value === "" ? "" : Number(value) }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.title?.trim() ||
      !form.location?.trim() ||
      !form.type?.trim() ||
      !form.openings ||
      !form.description?.trim()
    ) {
      setError("All required fields must be filled.");
      setSuccess("");
      return;
    }

    const updatedJob = {
      title: form.title.trim(),
      department: form.department?.trim() || null,
      location: form.location.trim(),
      locationType: form.locationType,
      type: form.type,
      openings: Number(form.openings),
      description: form.description.trim(),
      keyResponsibilities: form.keyResponsibilities?.trim() || null,
      validityDays: Number(form.validityDays) || 30,
    };

    try {
      await onSave(updatedJob);
      setSuccess("Job updated!");
      setError("");
    } catch (err) {
      setError("Failed to update job");
      setSuccess("");
    }
  };

  if (!open) return null;

  return (
    <div className="modal">
      <form onSubmit={handleSubmit}>
        <div className="modal-header">
          <h2>Edit Job Posting</h2>
          
        </div>

        <label>
          Title *
          <input
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Title"
            required
          />
        </label>

        <label>
          Department
          <input
            name="department"
            value={form.department}
            onChange={handleChange}
            placeholder="Department"
          />
        </label>

        <label>
          Location *
          <input
            name="location"
            value={form.location}
            onChange={handleChange}
            placeholder="Location"
            required
          />
        </label>

        <label>
          Location Type
          <select name="locationType" value={form.locationType} onChange={handleChange} required>
            <option value="ONSITE">Onsite</option>
            <option value="REMOTE">Remote</option>
            <option value="HYBRID">Hybrid</option>
          </select>
        </label>

        <label>
          Type *
          <select name="type" value={form.type} onChange={handleChange} required>
            <option value="FULL_TIME">Full-time</option>
            <option value="PART_TIME">Part-time</option>
            <option value="CONTRACT">Contract</option>
            <option value="INTERNSHIP">Internship</option>
          </select>
        </label>

        <label>
          Openings *
          <input
            name="openings"
            type="number"
            min="1"
            value={form.openings}
            onChange={handleChange}
            required
          />
        </label>

        <label>
          Description *
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Description"
            required
          />
        </label>

        <label>
          Key Responsibilities
          <textarea
            name="keyResponsibilities"
            value={form.keyResponsibilities}
            onChange={handleChange}
            placeholder="Key Responsibilities"
          />
        </label>

        <label>
          Validity Days
          <input
            name="validityDays"
            type="number"
            min="1"
            value={form.validityDays}
            onChange={handleChange}
          />
        </label>

        <h3>Requirements (view only)</h3>

        <label>
          Minimum Education
          <input value={requirements.education.minimum} readOnly />
        </label>

        <label>
          Preferred Education
          <input value={requirements.education.preferred} readOnly />
        </label>

        <label>
          Minimum Years Experience
          <input type="number" value={requirements.experience.minimumYears} readOnly />
        </label>

        <label>
          Preferred Years Experience
          <input type="number" value={requirements.experience.preferredYears} readOnly />
        </label>

        <label>
          Mandatory Certifications
          <input value={requirements.certifications.mandatory.join(", ")} readOnly />
        </label>

        <label>
          Preferred Certifications
          <input value={requirements.certifications.preferred.join(", ")} readOnly />
        </label>

        <label>
          Mandatory Skills
          <input value={requirements.skills.mandatory.join(", ")} readOnly />
        </label>

        <label>
          Preferred Skills
          <input value={requirements.skills.preferred.join(", ")} readOnly />
        </label>

        <label>
          Other Requirements
          <input value={requirements.other} readOnly />
        </label>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        <div className="modal-actions">
          <button type="submit">Update</button>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditJobModal;
